import { useRef, useState } from "react";
import { Camera, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { checklistMessage, isComplete, type ChecklistEntry, type ChecklistItem } from "./checklistItems";

export function UnderwritingChecklist({ business, items, sending, onSend }: {
  business: string;
  items: ChecklistItem[];
  sending: boolean;
  onSend: (body: string, files: File[]) => Promise<boolean>;
}) {
  const [entries, setEntries] = useState<Record<string, ChecklistEntry>>({});
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const entry = (id: string) => entries[id] ?? { files: [], answer: "", done: false };
  const update = (id: string, patch: Partial<ChecklistEntry>) =>
    setEntries((prev) => ({ ...prev, [id]: { ...entry(id), ...prev[id], ...patch } }));

  const allDone = items.every((item) => isComplete(item, entries[item.id]));

  async function send() {
    const files = items.flatMap((item) => entries[item.id]?.files ?? []);
    if (await onSend(checklistMessage(business, items, entries), files)) setEntries({});
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-3">
      <p className="text-sm font-semibold text-slate-800">Underwriting needs:</p>
      <ul className="mt-1 divide-y divide-slate-100">
        {items.map((item) => {
          const e = entry(item.id);
          const done = isComplete(item, entries[item.id]);
          return (
            <li key={item.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border", done ? "border-green-600 bg-green-600 text-white" : "border-slate-300")}>
                {done && <Check className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0 flex-1 text-sm">
                <div>{item.label}{item.hint && <span className="ml-1 text-xs text-slate-500">({item.hint})</span>}</div>
                {item.kind === "upload" && e.files.length > 0 && (
                  <div className="text-xs text-slate-500">{e.files.map((f) => `📎 ${f.name}`).join("  ")}</div>
                )}
                {item.kind === "answer" && (
                  <Input
                    className="mt-1 h-8 max-w-sm"
                    placeholder={item.id === "call-times" ? "e.g. Tue–Thu after 10 AM" : "Type the answer"}
                    value={e.answer}
                    onChange={(ev) => update(item.id, { answer: ev.target.value })}
                  />
                )}
              </div>
              {item.kind === "upload" && (
                <>
                  <Button type="button" size="sm" variant="outline" onClick={() => inputs.current[item.id]?.click()}>
                    <Camera className="mr-1 h-4 w-4" /> {e.files.length ? "Add more" : "Add photo"}
                  </Button>
                  <input
                    ref={(el) => { inputs.current[item.id] = el; }}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    hidden
                    onChange={(ev) => { update(item.id, { files: [...e.files, ...Array.from(ev.target.files ?? [])] }); ev.target.value = ""; }}
                  />
                </>
              )}
              {item.kind === "action" && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={e.done} onCheckedChange={(v) => update(item.id, { done: v === true })} /> Done
                </label>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={send} disabled={!allDone || sending}>
          {sending ? "Sending…" : "Send to underwriting"}
        </Button>
        <span className="text-xs text-slate-500">Goes straight to underwriting. Back office sees it here too.</span>
      </div>
    </div>
  );
}
